import { CustomAsyncStorage as AsyncStorage } from './customAsyncStorage/customAsyncStorage';
import { z, SafeParseError} from 'zod';

const AccessTokenObj = z.object({
    expiresAt: z.string(),
    expiresAtMs: z.number(),
    expiresIn: z.string(),
    expiresInMs: z.number(),
    token: z.string(),
}).strict();

const TokenObj = z.object({
    refreshToken: z.string(),
    accessToken: AccessTokenObj,
}).strict();

interface Token {
    refreshToken: string,
    accessToken: {
        expiresAt: string,
        expiresAtMs: number,
        expiresIn: string,
        expiresInMs: number,
        token: string,
    }
}

type RefreshAccessTokenFunction = (accessToken: string, refreshToken: string, skipThrows?: boolean | undefined) => Promise<Token>;


let isRefreshInProgress = false;
let inProgressRefreshPromise: Promise<string> | undefined;
let asyncStorageKey = 'token';
let refreshAccessTokenFunction: RefreshAccessTokenFunction | undefined;

const refreshAccessToken = async (tokens: Token, refreshFunction: RefreshAccessTokenFunction) => {
    let newAccessToken = await refreshFunction(tokens.accessToken.token, `Bearer ${tokens.refreshToken}`);
    tokens.accessToken = newAccessToken.accessToken;
    tokens.refreshToken = newAccessToken.refreshToken;
    await AsyncStorage.setItem(asyncStorageKey, JSON.stringify(tokens));
    isRefreshInProgress = false;
    return `Bearer ${newAccessToken.accessToken.token}`;
};

const MB_accessTokenUtils = {
    setAccessTokenRefreshEndpointFunction: (tokenRefresherFunction: RefreshAccessTokenFunction) => {
        refreshAccessTokenFunction = tokenRefresherFunction;
    },

    /**
     * Sets the key that is used to store and retrieve the access token object from async storage.
     * Default key is `token`. There should be no need to call this function unless you are intending
     * to store some data in async storage with the same key of "token".
     */
    setAsyncStorageKey: (newAsyncStorageKey: string) => {
        asyncStorageKey = newAsyncStorageKey;
    },

    /**
     * Stores the token into async storage
     * @param tokens 
     */
    setAccessToken: async (token: Token) => {
        try {
            await AsyncStorage.setItem(asyncStorageKey, JSON.stringify(token));
        } catch (error) {
            throw error;
        }
    },
    /**
     * Removes the access token from async storage
     */
    removeAccessToken: async () => {
        try {
            await AsyncStorage.removeItem(asyncStorageKey);
        } catch (error) {
            throw error;
        }
    },

    /**
     * Checks to see if there is less than one minute untill the access token is expired.
     * If it already expires or expiry date is in less than 1 minute, refetches a new token
     * and returns the access token. Otherwise, just returns the access token.
     * @returns {string} Access token string
     */
    getAccessToken: async (): Promise<string | undefined> => {
        if (refreshAccessTokenFunction === undefined) {
            throw 'Error: MB_accessTokenUtils requires refreshAccessTokenFunction to be initialized via setAccessTokenRefreshEndpointFunction in order to be used';
        }

        try {
            const tokensString = await AsyncStorage.getItem(asyncStorageKey);
            if (!tokensString) {
                return undefined;
            }

            const tokens = JSON.parse(tokensString) as Token;

            const validateResult = TokenObj.safeParse(tokens);
            if (!validateResult.success) {
                let errMsg = `Data format validation error for getAccessToken \n\n`;
                console.error(errMsg, {
                    data: tokens,
                    issues: (validateResult as unknown as SafeParseError<any>)?.error.issues, // TODO: For some reason TS was complaining about this without the cast
                });
                throw 'CLIENT_DATA_MISMATCH_ERROR';
            }


            // Less than 1 min left for expiration, so we refetch first and then send.
            if (tokens.accessToken.expiresAtMs - Date.now() < 60000) {
                if (!isRefreshInProgress) {
                    isRefreshInProgress = true;
                    inProgressRefreshPromise = refreshAccessToken(tokens, refreshAccessTokenFunction);
                    return await inProgressRefreshPromise;
                }

                return await inProgressRefreshPromise;
            } else {
                return `Bearer ${tokens.accessToken.token}`;
            }
        } catch (error) {
            console.error('Error when getting the access token', error);
            throw error;
        }
    },
};


export { MB_accessTokenUtils };
export type { Token };
